import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { format, parse, isValid } from 'date-fns';
import { ca } from 'date-fns/locale';
import { useClickAway } from 'react-use';
import { useTranslation } from 'react-i18next';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';

// Types
import {
  ContainerProps,
  onChangeType,
  handleClickType,
  onBlurType,
} from './types';

// Styled Components
import {
  Container,
  SmallContainer,
  Label,
  InputDate,
  InputTime,
  Calendar,
  ContainerInputs,
  Span,
  InputNumberHours,
  InputNumberMinutes,
  ContainerCalendar,
  ClearDateButton,
} from './styles';
import { ErrorMessage } from '../InputText/styles';

// DayPicker Styles
import { DayPickerGlobalStyles } from '../Calendar/DayPicker';

/**
 * Los dos últimos dígitos tecleados, con cero delante si falta (`'7'` → `'07'`,
 * `'400'` → `'00'`), o null si no hay ninguno.
 *
 * Se mira lo ÚLTIMO tecleado y no el valor entero: con el cursor al final la
 * tecla se AÑADE a lo que había, y validar «400» antes de recortarlo
 * descartaba la tecla. Pasa al pulsar un campo que ya tenía el foco — el de
 * los minutos, tras el salto automático desde la hora —: el navegador deja el
 * cursor donde se pulsa en vez de la selección (QA 15-oct, T1.x+a: «00» en
 * los minutos no entraba tras cambiar la hora).
 */
const lastTwoDigits = (raw: string): string | null => {
  const digits = raw.replace(/\D/g, '');
  return digits ? digits.slice(-2).padStart(2, '0') : null;
};

export const DateTime = ({
  isDisabled,
  label,
  labelHour,
  onlyHour,
  dateInputName = 'date',
  hourInputName = 'hours',
  minutesInputName = 'minutes',
  onChangeDate,
  initialDate,
  error,
  borderError,
  value,
  toDate,
  fromDate,
  clearable,
  ...props
}: ContainerProps) => {
  const { t } = useTranslation(['buttons']);
  const today = new Date();
  const effectiveToDate = toDate ?? today;

  const [selected, setSelected] = useState<Date | undefined>(
    initialDate ? new Date(initialDate) : undefined
  );

  const [isZero, setIsZero] = useState(false);

  const [valueHours, setHours] = useState<string>(
    selected
      ? selected.getHours().toString().length === 1
        ? '0' + selected.getHours().toString()
        : selected.getHours().toString()
      : ''
  );
  const [valueMinutes, setMinutes] = useState<string>(
    selected
      ? selected.getMinutes().toString().length === 1
        ? '0' + selected.getMinutes().toString()
        : selected.getMinutes().toString()
      : ''
  );
  const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(
    selected ?? undefined
  );
  const [displayCalendar, setDisplayCalendar] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [manualInput, setManualInput] = useState<string>('');
  const [isManualEditing, setIsManualEditing] = useState(false);

  const formatValueDate = value
    ? format(new Date(value as string), 'dd/MM/yyyy')
    : undefined;

  const formHours = value ? format(new Date(value as string), 'HH') : undefined;
  const formMinutes = value
    ? format(new Date(value as string), 'mm')
    : undefined;

  const selectedDate = selected && format(selected, 'dd/MM/yyyy');

  const maxLength = 2;

  const inputRef = useRef(null);
  const minuteRef = useRef<HTMLInputElement>(null);
  const hourRef = useRef<HTMLInputElement>(null);
  const inputDateRef = useRef<HTMLInputElement>(null);
  const nextCursorDigitCountRef = useRef<number | null>(null);

  // Use this function to handle the change of the date with onChangeDate by sending a complete Date object
  const handleDateChange = useCallback(
    (dateCalendar?: Date) => {
      const valueHoursParsed = Number(valueHours);
      const valueMinutesParsed = Number(valueMinutes);
      if (selected instanceof Date) {
        if (dateCalendar) {
          if (valueHoursParsed === 0) {
            dateCalendar.setHours(0);
          } else {
            dateCalendar.setHours(valueHoursParsed || selected.getHours());
          }

          if (valueMinutesParsed === 0) {
            dateCalendar.setMinutes(0);
          } else {
            dateCalendar.setMinutes(
              valueMinutesParsed || selected.getMinutes()
            );
          }
          setSelected(dateCalendar);
          onChangeDate?.(dateCalendar.toISOString());
        } else {
          if (valueHoursParsed === 0) {
            selected.setHours(0);
          } else {
            selected.setHours(valueHoursParsed || selected.getHours());
          }

          if (valueMinutesParsed === 0) {
            selected.setMinutes(0);
          } else {
            selected.setMinutes(valueMinutesParsed || selected.getMinutes());
          }
          setSelected(selected);
          onChangeDate?.(selected.toISOString());
        }
      } else if (dateCalendar) {
        dateCalendar.setHours(valueHoursParsed);
        dateCalendar.setMinutes(valueMinutesParsed);
        setSelected(dateCalendar);
        onChangeDate?.(dateCalendar.toISOString());
      } else {
        onChangeDate?.();
      }
    },
    [valueHours, valueMinutes, selected]
  );

  useEffect(() => {
    handleDateChange();
  }, [selected, valueHours, valueMinutes]);

  const handleClickClose = useCallback(() => setDisplayCalendar(false), []);
  const handleClick = useCallback(
    () => setDisplayCalendar(!displayCalendar),
    []
  );
  const handleClickDayCalendar = useCallback(() => {
    setDisplayCalendar(false);
    hourRef.current?.focus();
    hourRef.current?.select();
  }, []);

  // Function to set the hour in InputTime, prevent values over 23

  const onChangeHours = useCallback<onChangeType>(
    (e) => {
      e.target.value === '0' && setIsZero(false);

      const newHour = lastTwoDigits(e.target.value);
      if (newHour !== null && parseInt(newHour) < 24) {
        if (
          (parseInt(valueHours) === 0 && !isZero) ||
          parseInt(newHour) > 2
        ) {
          e.target.blur();
          minuteRef.current?.focus();
          minuteRef.current?.select();
        }

        setHours(newHour);
      }
    },
    [valueHours, isZero]
  );

  // Function to set the minutes in InputTime, prevent values over 59
  const onChangeMinutes = useCallback<onChangeType>((e) => {
    const newMinutes = lastTwoDigits(e.target.value);
    if (newMinutes !== null && parseInt(newMinutes) < 60) {
      setMinutes(newMinutes);
    }
  }, []);

  const handleBlurHours = useCallback<onBlurType>(
    (e) => {
      valueHours === '00' && setIsZero(true);
      e.target.value === '' && setHours('00');
    },
    [valueHours, isZero]
  );

  const handleBlurMinutes = useCallback<onBlurType>((e) => {
    e.target.value === '' && setMinutes('00');
  }, []);

  //Function to select numbers in InputNumberHours and InputNumberMinutes

  const handleClickInput = useCallback<handleClickType>((e) => {
    const input = e.target as HTMLInputElement;
    input.select();
  }, []);

  // 🔴 Sin esto, pulsar un campo que YA tiene el foco no lo selecciona: el
  // navegador resuelve el cursor al soltar el ratón y deshace el `select()`
  // del clic (medido en Chromium con un `type="number"`: «15» + «0» = «150»).
  // Es justo lo que pasa con los minutos tras el salto automático desde la
  // hora (QA 15-oct, T1.x+a). Sin el ratón no hay cursor que colocar: el
  // campo entero se selecciona igual en cada toque.
  const keepSelectionOnMouseUp = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => e.preventDefault(),
    []
  );

  const onCloseCalendar = useCallback(() => {
    setDisplayCalendar(false);
  }, []);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ' ') {
      event.preventDefault();
      setDisplayCalendar(!displayCalendar);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const isDateWithinBounds = (date: Date): boolean => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const max = new Date(effectiveToDate.getFullYear(), effectiveToDate.getMonth(), effectiveToDate.getDate());
    if (d > max) return false;
    if (fromDate) {
      const min = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
      if (d < min) return false;
    }
    return true;
  };

  const applyManualDate = useCallback(
    (raw: string) => {
      const parsed = parse(raw, 'dd/MM/yyyy', new Date());
      if (isValid(parsed) && parsed.getFullYear() >= 1900 && isDateWithinBounds(parsed)) {
        setIsManualEditing(false);
        setManualInput('');
        setSelected(parsed);
        setCalendarMonth(parsed);
        handleDateChange(parsed);
      }
    },
    [handleDateChange]
  );

  const handleDateInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const cursorPos = e.target.selectionStart ?? rawValue.length;

      nextCursorDigitCountRef.current = rawValue
        .slice(0, cursorPos)
        .replace(/[^\d]/g, '').length;

      const digitsOnly = rawValue.replace(/[^\d]/g, '');

      let formatted = digitsOnly;
      if (digitsOnly.length > 2) {
        formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
      }
      if (digitsOnly.length > 4) {
        formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
      }

      setManualInput(formatted);
      setIsManualEditing(true);

      if (formatted.length === 10) {
        applyManualDate(formatted);
      }
    },
    [applyManualDate]
  );

  useLayoutEffect(() => {
    if (nextCursorDigitCountRef.current === null || !inputDateRef.current) return;

    const input = inputDateRef.current;
    const val = input.value;
    const targetDigitsBefore = nextCursorDigitCountRef.current;
    nextCursorDigitCountRef.current = null;

    if (targetDigitsBefore === 0) {
      input.setSelectionRange(0, 0);
      return;
    }

    let pos = val.length;
    let digitCount = 0;
    for (let i = 0; i < val.length; i++) {
      if (/\d/.test(val[i])) {
        digitCount++;
        if (digitCount === targetDigitsBefore) {
          pos = i + 1;
          while (pos < val.length && !/\d/.test(val[pos])) pos++;
          break;
        }
      }
    }

    input.setSelectionRange(pos, pos);
  }, [manualInput]);

  const handleDateInputBlur = useCallback(() => {
    if (isManualEditing && manualInput.length === 10) {
      applyManualDate(manualInput);
    }
    setIsManualEditing(false);
    setManualInput('');
  }, [isManualEditing, manualInput, applyManualDate]);

  // Vacía una fecha YA establecida (solo cuando `clearable`). Antes NO había
  // forma de quitar una fecha: el input solo aceptaba fechas válidas de 10
  // dígitos y, al vaciarlo y hacer blur, el display revertía a `selected` →
  // parecía "rellenarse sola". Resetea todo el estado interno y notifica al
  // padre con undefined (RHF deja el campo vacío). El
  // useEffect([selected, valueHours, valueMinutes]) también dispararía
  // handleDateChange() con undefined, pero limpiamos aquí para que sea inmediato.
  const handleClearDate = useCallback(() => {
    setSelected(undefined);
    setCalendarMonth(undefined);
    setHours('');
    setMinutes('');
    setManualInput('');
    setIsManualEditing(false);
    setIsZero(false);
    setDisplayCalendar(false);
    onChangeDate?.();
  }, [onChangeDate]);

  const isLandscapeTablet = useIsLandscapeTablet();

  useClickAway(inputRef, () => setDisplayCalendar(false));

  const inputId = props.id || `date-input-${dateInputName}`;
  const hourId = `hour-input-${hourInputName}`;

  return (
    <Container ref={inputRef} {...props}>
      <DayPickerGlobalStyles />
      <ContainerInputs className="container-inputs">
        {!onlyHour && (
          <>
            <SmallContainer>
              {label && (
                <Label component="label" size="S" htmlFor={inputId}>
                  {label}
                </Label>
              )}
              <InputDate
                ref={inputDateRef}
                id={inputId}
                name={dateInputName}
                type="text"
                autoComplete="off"
                inputMode={isLandscapeTablet ? undefined : 'numeric'}
                readOnly={isLandscapeTablet}
                placeholder="dd/mm/aaaa"
                value={
                  isManualEditing
                    ? manualInput
                    : (formatValueDate ?? (selectedDate || ''))
                }
                onClick={handleClick}
                onChange={isLandscapeTablet ? undefined : handleDateInputChange}
                onBlur={isLandscapeTablet ? undefined : handleDateInputBlur}
                disabled={isDisabled}
                borderError={borderError}
                onKeyDown={isLandscapeTablet ? undefined : handleKeyDown}
                className={'input-date'}
              />
            </SmallContainer>
            <ContainerCalendar>
              {displayCalendar && (
                <Calendar
                  toDate={effectiveToDate}
                  fromDate={fromDate}
                  mode="single"
                  locale={ca}
                  selected={selected}
                  onSelect={setSelected}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  numberOfMonths={1}
                  onDayClick={handleClickDayCalendar}
                />
              )}
            </ContainerCalendar>
          </>
        )}

        <SmallContainer>
          {labelHour && (
            <Label component="label" size="S" htmlFor={hourId}>
              {labelHour}
            </Label>
          )}
          <InputTime
            className="input-hour"
            onClick={handleClickClose}
            aria-disabled={isDisabled}
            borderError={borderError}
            isFocus={isFocus}
            onFocus={() => setIsFocus(true)}
            onBlur={() => setIsFocus(false)}
          >
            <InputNumberHours
              id={hourId}
              name={hourInputName}
              ref={hourRef}
              placeholder="00"
              type={'number'}
              maxLength={maxLength}
              max={23}
              min={0}
              onChange={onChangeHours}
              onClick={handleClickInput}
              onMouseUp={keepSelectionOnMouseUp}
              onKeyDown={(e) =>
                (e.key === 'Backspace' || e.key === 'Delete') && setHours('')
              }
              onFocus={() => setDisplayCalendar(false)}
              onBlur={handleBlurHours}
              disabled={isDisabled}
              value={formHours ?? valueHours}
              tabIndex={0}
              className={'input-number'}
            />
            <Span>:</Span>
            <InputNumberMinutes
              name={minutesInputName}
              ref={minuteRef}
              placeholder="00"
              type={'number'}
              maxLength={maxLength}
              max={59}
              min={0}
              onChange={onChangeMinutes}
              onClick={handleClickInput}
              onMouseUp={keepSelectionOnMouseUp}
              onKeyDown={(e) =>
                (e.key === 'Backspace' || e.key === 'Delete') && setMinutes('')
              }
              onBlur={handleBlurMinutes}
              disabled={isDisabled}
              value={formMinutes ?? valueMinutes}
              tabIndex={0}
              className={'input-number'}
            />
          </InputTime>
        </SmallContainer>

        {/*
          Papelera para vaciar una fecha opcional ya establecida. Solo se
          muestra si `clearable` está activo Y hay una fecha (`selected`) que
          quitar. `type="button"` evita que dispare el submit del form.
          Mismo botón canónico que el filtro de Gestión Horaria.
        */}
        {clearable && !isDisabled && !!selected && (
          <ClearDateButton
            variant="delete"
            type="button"
            onClick={handleClearDate}
            title={t('clear')}
            data-testid="date-clear-btn"
          />
        )}
      </ContainerInputs>
      <ContainerCalendar>
        {displayCalendar && (
          <Calendar
            toDate={effectiveToDate}
            fromDate={fromDate}
            mode="single"
            locale={ca}
            selected={selected}
            onSelect={handleDateChange}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            numberOfMonths={1}
            onDayClick={handleClickDayCalendar}
            onClose={onCloseCalendar}
          />
        )}
      </ContainerCalendar>
      {error && (
        <ErrorMessage colorText={'lightCoralRed'} size={'XXS'}>
          {error}
        </ErrorMessage>
      )}
    </Container>
  );
};
